import { observer } from 'mobx-react-lite'
import { ConfirmSheet } from '@/components/ConfirmSheet'
import { FilterEditorContainer } from '@/components/FilterControls/FilterControlsContainer'
import { type JobListTabsViewModel } from '@/features/jobs/list/JobListTabsViewModel'
import { type SavedJobFilterEditorViewModel } from '@/features/jobs/list/SavedJobFilterEditorViewModel'
import {
  maxSavedJobFilterNameLength,
  type SavedJobFilter,
  sharedSavedFilterId,
} from '@/features/jobs/list/saved-job-filters'
import { SavedFilterEditor } from './SavedFilterEditor'
import { SavedFilterPeek } from './SavedFilterPeek'
import {
  DefaultTabMenu,
  DeleteTabConfirmContent,
  OwnTabMenu,
  ResetTabsConfirmContent,
  SavedFilterTabs,
} from './SavedFilterTabs'

export interface SavedFilterTabsContainerProps {
  vm: JobListTabsViewModel
}

export const SavedFilterTabsContainer = observer(
  function SavedFilterTabsContainer({ vm }: SavedFilterTabsContainerProps) {
    const shared = vm.sharedTab
    const menuTab = vm.tabs.find((t) => t.id === vm.menuTabId)
    const deletingTab = vm.allTabs.find((t) => t.id === vm.deletingTabId)
    const duplicate = (id: string) =>
      vm.canAddTab ? () => vm.duplicate(id) : undefined
    const removable = (action: () => void) =>
      menuTab && vm.canRemove(menuTab.id) ? action : undefined

    return (
      <>
        <SavedFilterTabs
          nameMaxLength={maxSavedJobFilterNameLength}
          tabs={[
            ...vm.tabs.map((tab) => ({
              id: tab.id,
              name: tab.name,
              isSelected: tab.id === vm.selectedTabId,
              peek: <SavedFilterPeekContainer vm={vm} tab={tab} />,
            })),
            ...(shared
              ? [
                  {
                    id: sharedSavedFilterId,
                    name: shared.name,
                    isSelected: vm.selectedTabId === sharedSavedFilterId,
                    isShared: true,
                    peek: null,
                  },
                ]
              : []),
          ]}
          menuTabId={vm.menuTabId}
          renamingTabId={vm.renamingTabId}
          editingTabId={vm.editor?.savedFilterId ?? null}
          menu={
            menuTab &&
            (menuTab.isDefault ? (
              <DefaultTabMenu
                onDuplicate={duplicate(menuTab.id)}
                onHide={removable(() => vm.hide(menuTab.id))}
                onDelete={removable(() => vm.requestDelete(menuTab.id))}
              />
            ) : (
              <OwnTabMenu
                onEdit={() => vm.edit(menuTab.id)}
                onRename={() => vm.startRenaming(menuTab.id)}
                onDuplicate={duplicate(menuTab.id)}
                onCopyLink={() => void vm.copyLink(menuTab.id)}
                onHide={removable(() => vm.hide(menuTab.id))}
                onDelete={removable(() => vm.requestDelete(menuTab.id))}
              />
            ))
          }
          editor={
            vm.editor && (
              <SavedFilterEditorContainer
                vm={vm.editor}
                jobCount={vm.editorJobCount}
                onCancel={vm.closeEditor}
              />
            )
          }
          onSelect={vm.select}
          onOpenMenu={vm.openMenu}
          onCloseMenu={vm.closeMenu}
          onCloseEditor={vm.closeEditor}
          onRename={vm.rename}
          onCancelRename={vm.cancelRenaming}
          onMove={vm.move}
        />
        <ConfirmSheet
          danger
          yes="Delete tab"
          showing={!!deletingTab}
          onConfirm={vm.confirmDelete}
          onDecline={vm.cancelDelete}
          confirmButtonTestId="job-list-saved-filter-delete-confirm"
        >
          {deletingTab && <DeleteTabConfirmContent name={deletingTab.name} />}
        </ConfirmSheet>
        <ConfirmSheet
          yes="Reset tabs"
          showing={vm.isConfirmingReset}
          onConfirm={vm.confirmReset}
          onDecline={vm.cancelReset}
          confirmButtonTestId="job-list-reset-tabs-confirm"
        >
          <ResetTabsConfirmContent />
        </ConfirmSheet>
      </>
    )
  },
)

interface SavedFilterPeekContainerProps {
  vm: JobListTabsViewModel
  tab: SavedJobFilter
}

const SavedFilterPeekContainer = observer(function SavedFilterPeekContainer({
  vm,
  tab,
}: SavedFilterPeekContainerProps) {
  return (
    <SavedFilterPeek
      name={tab.name}
      description={tab.isDefault ? vm.describeDefault(tab.id) : null}
      startsFrom={vm.baseNameOf(tab)}
      filters={vm.describe(tab)}
      onDuplicate={vm.canAddTab ? () => vm.duplicate(tab.id) : undefined}
      onEdit={tab.isDefault ? undefined : () => vm.edit(tab.id)}
    />
  )
})

interface SavedFilterEditorContainerProps {
  vm: SavedJobFilterEditorViewModel
  jobCount: number | null
  onCancel: () => void
}

const SavedFilterEditorContainer = observer(
  function SavedFilterEditorContainer({
    vm,
    jobCount,
    onCancel,
  }: SavedFilterEditorContainerProps) {
    return (
      <SavedFilterEditor
        name={vm.name}
        nameMaxLength={maxSavedJobFilterNameLength}
        onNameChange={vm.setName}
        startsFrom={vm.base?.name ?? null}
        rules={vm.filters.explicitFilterVms.map((filterVm) => ({
          key: filterVm.key,
          field: filterVm.title,
          valueText: filterVm.valueText,
          isEditing: filterVm.editing,
          valueEditor: (
            <FilterEditorContainer
              filter={filterVm}
              testId={`job-list-saved-filter-rule-${filterVm.key}`}
            />
          ),
        }))}
        addableFields={vm.addableFilters.map((f) => ({
          key: f.key,
          title: f.title,
        }))}
        onAdd={vm.addFilter}
        onChangeField={vm.replaceFilter}
        onRemove={vm.removeFilter}
        onEditValue={vm.editValue}
        onStopEditingValues={vm.stopEditingValues}
        jobCount={jobCount}
        canSave={vm.canSave}
        onCancel={onCancel}
        onSave={vm.save}
      />
    )
  },
)
